import React, { createContext, useContext, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const { token, user } = useAuth();

  useEffect(() => {
    if (token && user) {
      // Initialize socket connection
      socketRef.current = io(window.location.origin, {
        auth: { token }
      });

      socketRef.current.on('connect', () => {
        console.log('Socket connected');
        socketRef.current.emit('authenticate', token);
      });

      socketRef.current.on('disconnect', () => {
        console.log('Socket disconnected');
      });

      socketRef.current.on('error', (error) => {
        console.error('Socket error:', error);
        toast.error(error.message || 'Connection error');
      });

      // Listen for agent updates
      socketRef.current.on('agent:status', (data) => {
        console.log('Agent status update:', data);
      });

      socketRef.current.on('agent:response', (data) => {
        toast.success(`${data.agentName} responded`);
      });

      // Listen for task updates
      socketRef.current.on('task:created', (data) => {
        toast.success(`New task assigned to ${data.assignedTo?.name}`);
      });

      socketRef.current.on('task:completed', (data) => {
        toast.success('Task completed!');
      });

      socketRef.current.on('task:error', (data) => {
        toast.error(`Task failed: ${data.error}`);
      });

      // Listen for chat messages
      socketRef.current.on('chat:message', (data) => {
        // Handle new chat message
      });

      // Listen for inference status updates
      socketRef.current.on('inference:status', (data) => {
        console.log('Inference status:', data);
      });

      // Listen for budget alerts
      socketRef.current.on('budget:alert', (data) => {
        if (data.level === 'critical') {
          toast.error(data.message);
        } else if (data.level === 'warning') {
          toast(data.message, { icon: '⚠️' });
        }
      });

      // Listen for external task updates
      socketRef.current.on('external:task:update', (data) => {
        toast.success(`External task update: ${data.status}`);
      });

      // Listen for OpenClaw messages
      socketRef.current.on('openclaw:message', (data) => {
        toast(`OpenClaw: ${data.content?.substring(0, 50)}...`, { icon: '🤖' });
      });

      return () => {
        socketRef.current?.disconnect();
      };
    }
  }, [token, user]);

  const sendAgentMessage = (agentId, message, context = {}) => {
    socketRef.current?.emit('agent:message', { agentId, message, context });
  };

  const updateTask = (taskId, updates) => {
    socketRef.current?.emit('task:update', { taskId, updates });
  };

  const sendChatMessage = (chatId, content, mentions = []) => {
    socketRef.current?.emit('chat:message', { chatId, content, mentions });
  };

  const value = {
    socket: socketRef.current,
    sendAgentMessage,
    updateTask,
    sendChatMessage
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export default SocketContext;
